import * as Sentry from '@sentry/browser';
import i18n from 'i18next';

import {
  Account,
  InputPreprocessors,
  InputTypes,
  PaymentAttribute,
  PaymentMethodConfiguration,
  PaymentMethodInput,
  PliromyPreprocessorParameters,
  PreprocessorParam,
} from '@cashier/cashier-model';

import { DataConverter } from './dataConverter';

const getPliromyInputs = (): PaymentMethodInput[] => [
  {
    name: 'cardHolder',
    label: i18n.t('cardHolder.label'),
    required: true,
    type: InputTypes.Input,
    placeholder: i18n.t('cardHolder.placeholder'),
    value: null,
    params: {
      options: [],
      preprocessed: false,
      shouldBeStripped: true,
    },
  },
  {
    name: 'creditcardNumber',
    label: i18n.t('creditcardNumber.label'),
    required: true,
    type: InputTypes.CreditCardNumber,
    placeholder: i18n.t('creditcardNumber.placeholder'),
    value: null,
    params: {
      options: [],
      preprocessed: true,
      shouldBeStripped: true,
    },
  },
  {
    name: 'cvv',
    label: i18n.t('cvv.label'),
    required: true,
    type: InputTypes.Cvv,
    placeholder: i18n.t('cvv.placeholder'),
    value: null,
    params: {
      options: [],
      preprocessed: true,
      shouldBeStripped: true,
    },
  },
  {
    name: 'expirationDate',
    label: i18n.t('expirationDate.label'),
    required: true,
    type: InputTypes.DateTime,
    placeholder: i18n.t('expirationDate.placeholder'),
    value: null,
    mask: '99/99',
    params: {
      options: [],
      preprocessed: true,
      shouldBeStripped: true,
      yearFormat: 'YY',
    },
  },
];

export const shouldTokenize = (preprocessor: PreprocessorParam) => {
  return preprocessor?.type === InputPreprocessors.PLIROMY_TOKENIZER;
};

const attributesToFilter = ['token', 'cardMask'];

export const PliromyService = {
  enrichInputsWithCardItems(
    method: PaymentMethodConfiguration,
    showMethodInputs: boolean,
  ): PaymentMethodInput[] {
    const { inputs, account_form_inputs } = method;

    if (showMethodInputs) {
      const filteredInputs = inputs.filter(
        (input) => input.name !== 'cvv' && input.name !== 'cardHolder',
      );
      return [...filteredInputs, ...getPliromyInputs()];
    }

    // without map we cant get error for cvv field
    return account_form_inputs.map((el) =>
      el.name === InputTypes.Cvv ? { ...el, type: InputTypes.Cvv } : el,
    );
  },

  filterAttributes(attributes: PaymentAttribute[]): PaymentAttribute[] {
    return attributes.filter(
      (attribute) => !attributesToFilter.includes(attribute.name),
    );
  },

  async tokenizeCredentials(
    paymentInputs: Record<string, PaymentMethodInput>,
    preprocessor: PreprocessorParam,
  ): Promise<{ token: string; cvv: string; cardHolder: string }> {
    try {
      const { preprocessorUrl } = preprocessor.parameters;
      const credentials = DataConverter.toAuthToken(
        preprocessor.parameters as PliromyPreprocessorParameters,
      );
      const cardData = DataConverter.toTokenizePayload(paymentInputs);

      const response = await fetch(preprocessorUrl, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cardData),
      });

      if (!response.ok) {
        throw new Error(
          `Tokenization failed: ${response.status} ${response.statusText}`,
        );
      }

      const result = await response.json();

      if (!result.token) {
        throw new Error('No token received from pliromy service');
      }

      return {
        token: result.token,
        cvv: String(paymentInputs.cvv?.value),
        cardHolder: String(paymentInputs.cardHolder?.value),
      };
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          error,
        },
      });

      throw error;
    }
  },

  async getPreprocessedAttributes(
    paymentInputs: Record<string, PaymentMethodInput>,
    paymentMethod: PaymentMethodConfiguration,
    account?: Account,
  ): Promise<{ token?: string; cardMask?: string; cvv: string }> {
    let attributes = {};

    if (
      paymentMethod?.inputs?.some((el) => el.name === 'cardMask') && // cardMask has ONLY in withdrawal Pliromy
      paymentInputs.creditcardNumber
    ) {
      attributes = {
        ...attributes,
        cardMask: DataConverter.toMaskedCardNumber(
          paymentInputs.creditcardNumber.value as string,
        ),
      };
    }

    if (account?.value) {
      return { ...attributes, cvv: String(paymentInputs.cvv?.value) };
    }

    return {
      ...attributes,
      ...(await this.tokenizeCredentials(
        paymentInputs,
        paymentMethod.preprocessor,
      )),
    };
  },
};
